import { useEffect, useState } from "react";
import { Loader2, Send, Mail, TestTube2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import api from "../../lib/api";

const inp = "bg-black/30 border-white/15 text-white mt-1";

export default function AdminMarketing() {
  const [form, setForm] = useState({ subject: "", heading: "", body: "", audience: "all", campaign_id: "", test_email: "" });
  const [campaigns, setCampaigns] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadHistory = () => api.get("/admin/marketing/campaigns").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/promotions").then(({ data }) => setCampaigns(data)).catch(() => {});
    loadHistory();
  }, []);

  const sendTest = async () => {
    if (!form.test_email) { toast.error("Enter a test email"); return; }
    setBusy(true);
    try { const { data } = await api.post("/admin/marketing/test", form); toast.success(`Test ${data.status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };

  const send = async () => {
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    if (!window.confirm("Send this campaign to the selected subscribers now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/send", form);
      toast.success(`Sent to ${data.sent} subscriber(s)${data.failed ? `, ${data.failed} failed` : ""}.`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Marketing Emails</h1>
        <p className="mt-1 text-muted-foreground text-sm">Send updates to your VIP mailing list. Unsubscribed and non-consenting users are automatically excluded.</p>
      </div>

      <GlassCard className="p-6 space-y-4" hover={false}>
        <div><Label className="text-white/80">Subject</Label><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inp} data-testid="mkt-subject" /></div>
        <div><Label className="text-white/80">Heading</Label><Input value={form.heading} onChange={(e) => set("heading", e.target.value)} className={inp} data-testid="mkt-heading" /></div>
        <div><Label className="text-white/80">Body</Label><Textarea value={form.body} onChange={(e) => set("body", e.target.value)} className={`${inp} min-h-[160px]`} data-testid="mkt-body" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-white/80">Audience</Label>
            <select value={form.audience} onChange={(e) => set("audience", e.target.value)} className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1" data-testid="mkt-audience">
              <option value="all">All active subscribers</option>
              <option value="campaign">Subscribers of a campaign</option>
            </select>
          </div>
          {form.audience === "campaign" && (
            <div><Label className="text-white/80">Campaign</Label>
              <select value={form.campaign_id} onChange={(e) => set("campaign_id", e.target.value)} className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1">
                <option value="">Select…</option>
                {campaigns.map((c) => <option key={c.promotion_id} value={c.promotion_id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-white/10">
          <div className="flex-1 min-w-[220px]"><Label className="text-white/80">Test email</Label><Input value={form.test_email} onChange={(e) => set("test_email", e.target.value)} className={inp} placeholder="you@example.com" data-testid="mkt-test-email" /></div>
          <Button variant="outline" className="border-white/20 text-white gap-2" onClick={sendTest} disabled={busy} data-testid="mkt-send-test"><TestTube2 className="h-4 w-4" />Send test</Button>
          <Button className="cf-gold-btn gap-2" onClick={send} disabled={busy} data-testid="mkt-send"><Send className="h-4 w-4" />Send now</Button>
        </div>
      </GlassCard>

      <GlassCard className="p-6" hover={false}>
        <h2 className="text-white font-medium mb-3 flex items-center gap-2"><Mail className="h-4 w-4 text-gold" />Recent sends</h2>
        {history.length === 0 ? <p className="text-muted-foreground text-sm">No campaigns sent yet.</p> : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.mail_campaign_id} className="flex justify-between text-sm border-b border-white/5 py-2" data-testid="mkt-history-row">
                <span className="text-white/80">{h.subject}</span>
                <span className="text-white/50">{h.sent} sent{h.failed ? `, ${h.failed} failed` : ""} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
